import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {
    AnnualReportProvider,
    Errors,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumDialog, AumicoCoreLayoutProvider, DateLocalePipe, FIRST_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import { AccountMappingEngine, IAccountMappingFormInput } from '@modeso/aumico-lib-templates-fe';
import {
    AnnualReportStep,
    IAccount,
    IAccountMappingTemplate,
    IAnnualReport,
    IFinStatements,
    IFinancialStatementInterpretation,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, find, first, map } from 'rxjs/operators';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { EqualityChecksResult } from '../../../enums/equality-checks-results.enum';
import { ConsistencyCheckResult } from '../../../interfaces/consistency-check-result';
import { DataState } from '../../../shared/enum/datastate.enum';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { EqualityChecksHelper } from '../helpers/equality-checks.helper';
import { isReclassNewWorld } from '../helpers/newReclassesGate.helper';
import { TemplateLevelsChildrenHelper } from '../helpers/templateLevelsChildren.helper';
import { FinancialStatementMapper } from '../mappers/financial-statement-mapper';
import { templateConstants } from './../../../shared/template-constants';
import { IIncomeAccountSelectionDialogData } from './income-account-selection-popup/IIncomeAccountSelectionDialogData.interface';
import { IncomeAccountSelectionPopupComponent } from './income-account-selection-popup/income-account-selection-popup.component';

const debug = Debug('aumico:project-reports-fe:adjustments-and-reclasses.page');

const balanceFinStatementIndex = 0;
const incomeFinStatementIndex = 1;

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-adjustments-and-reclasses-page',
    templateUrl: 'adjustments-and-reclasses.page.html',
    styleUrls: ['adjustments-and-reclasses.page.scss'],
})
export class AdjustmentsAndReclassesPage extends AnnualReportSubPage implements OnInit {
    localizationKeys = localizationKeys;
    wordBeforeCurrencyKey =
        localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency;
    totalWordKey = localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_total;

    private set accountMappingInput(value: IAccountMappingFormInput) {
        this.prepareEngines(value);
    }

    currentAnnualReport: IAnnualReport | undefined;
    currentAnnualReportFinStatements: IFinStatements[];
    isBackendReturnError = false;
    backendErrors: string;
    readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;

    balanceEngine: AccountMappingEngine | undefined | null;
    private originalBalanceEngine: AccountMappingEngine | undefined | null;
    incomeEngine: AccountMappingEngine | undefined | null;
    private originalIncomeEngine: AccountMappingEngine | undefined | null;
    private IncomeAccountsOptions: IAccount[] = [];
    currency: string;
    shouldIncomeRecalculate = false;

    private isSaveRequestInProgress: boolean;

    private templateLevelsChildrenHelper: TemplateLevelsChildrenHelper;

    templateRowIdToRowChildrenIdsForBalance: Map<string, string[]>;
    templateRowIdToRowChildrenIdsForIncome: Map<string, string[]>;

    templateRowIdToRowParentsIdsForBalance: Map<string, Set<string>>;
    templateRowIdToRowParentsIdsForIncome: Map<string, Set<string>>;

    dataState: DataState = DataState.Loading;

    isPartOfArNavigation$: Observable<boolean>;

    /**
     * True when the report is on the new (transactional) reclass world
     * (`isReclassNewWorld`): either `reclassificationsVersion === 1`, or a
     * report that carries no legacy reclasses. In that case this legacy tab
     * renders the reclass column read-only and the user manages reclasses in
     * grouping & masking instead. For a legacy report that still has reclasses
     * this stays false, so the reclass column remains editable here.
     * Adjustment cells, balance cells and everything else stay editable in
     * every case.
     */
    public readonlyReclasses = false;

    constructor(
        injector: Injector,
        dialog: MatDialog,
        private annualReportProvider: AnnualReportProvider,
        private aumDialog: AumDialog,
        private financialStatementMapper: FinancialStatementMapper,
        protected override actionsListener$: ActionsSubject,
        protected override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private dateLocalPipe: DateLocalePipe,
        private readonly mixpanelService: MixpanelService,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.ADJUSTMENTS_RECLASSIFICATION;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(
            AnnualReportStep.ADJUSTMENTS_RECLASSIFICATION,
        );

        this.templateLevelsChildrenHelper = new TemplateLevelsChildrenHelper();
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.ADJ_AND_REC_TIME_SPENT_TILL_COMPLETION);

        this.dataState = DataState.Loading;

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        combineLatest([
            this.annualReportProvider.getActiveAnnualReport$(),
            this.annualReportProvider.getActiveFinStatements$(),
        ])
            .pipe(
                map(([activeAnnualReport, finStatements]: [IAnnualReport | undefined | null, IFinStatements[]]) => {
                    if (
                        activeAnnualReport != null &&
                        activeAnnualReport.generalInformation != null &&
                        finStatements.length >= templateConstants.financialStatementMinimumLength &&
                        finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template != null &&
                        finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                    ) {
                        return { activeAnnualReport, finStatements };
                    } else {
                        // Return null, so that we we this state can be handeled
                        return { activeAnnualReport: null, finStatements: [] };
                    }
                }),

                untilDestroyed(this),
            )
            .subscribe(({ activeAnnualReport, finStatements }) => {
                return this.handleGetActiveAnnualReport(activeAnnualReport, finStatements);
            });

        super.ngOnInit();

        this.annualReportProvider
            .getError$()
            .pipe(untilDestroyed(this))
            .subscribe((error: HttpErrorResponse) => this.handleErrors(error));
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }

    protected handleErrors(error: HttpErrorResponse): void {
        debug(error, 'adj. & reclasses tab error');

        if (error == null || error.status !== 401) {
            this.isBackendReturnError = false;
        } else {
            this.isBackendReturnError = true;
            this.backendErrors = Errors.ServerError;
            this.dataState = DataState.Error;
        }
    }

    private handleGetActiveAnnualReport(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        if (annualReport === null) {
            this.dataState = DataState.Error;
            return;
        }

        this.currentAnnualReport = cloneDeep(annualReport);
        this.currentAnnualReportFinStatements = cloneDeep(finStatements);
        // REV-XXXXX: derive the read-only gate from the AR's creation date
        // and the configured cutover. Recomputed on every AR refresh so
        // edits to `createdAt` (rare but possible via admin tooling) or
        // environment changes between renders pick up correctly.
        this.readonlyReclasses = isReclassNewWorld(this.currentAnnualReport);
        // we take a copy of annualReport in another variable to avoid reference issue
        const annualReportToModify = cloneDeep(annualReport);
        this.createAccountMappingInput(annualReportToModify, finStatements);
        this.prepareRelationsAmongLevelsIdsOfTemplates(finStatements);
    }

    private prepareRelationsAmongLevelsIdsOfTemplates(finStatements: IFinStatements[]): void {
        const balanceTemplateRowIdRelations = this.templateLevelsChildrenHelper.getRelationsAmongLevelsOfTemplate(
            finStatements[balanceFinStatementIndex].templateAndAccountsMapping.template as IAccountMappingTemplate,
        );
        this.templateRowIdToRowChildrenIdsForBalance = balanceTemplateRowIdRelations.groupsLevelIdToLevelChildrenId;
        this.templateRowIdToRowParentsIdsForBalance = balanceTemplateRowIdRelations.groupsLevelsIdToParentsLevelsId;
        const incomeTemplateRowIdRelations = this.templateLevelsChildrenHelper.getRelationsAmongLevelsOfTemplate(
            finStatements[incomeFinStatementIndex].templateAndAccountsMapping.template as IAccountMappingTemplate,
        );
        this.templateRowIdToRowChildrenIdsForIncome = incomeTemplateRowIdRelations.groupsLevelIdToLevelChildrenId;
        this.templateRowIdToRowParentsIdsForIncome = incomeTemplateRowIdRelations.groupsLevelsIdToParentsLevelsId;
    }

    private updateCurrentAnnualReport(
        finStatements: IFinStatements[],
        balanceEngine: AccountMappingEngine,
        incomeEngine: AccountMappingEngine,
    ): void {
        if (this.shouldIncomeRecalculate) {
            incomeEngine.calculateAll(true);
        }
        // check boolean if true recalculate
        const balanceAccounts = balanceEngine.getAllAccounts();
        const balanceTemplate = balanceEngine.getMappedTemplate();

        const incomeAccounts = incomeEngine.getAllAccounts();
        const incomeTemplate = incomeEngine.getMappedTemplate();

        if (balanceAccounts != null && balanceTemplate != null) {
            finStatements[balanceFinStatementIndex].templateAndAccountsMapping.accounts = balanceAccounts;
            finStatements[balanceFinStatementIndex].templateAndAccountsMapping.template = balanceTemplate;
        }
        if (incomeAccounts != null && incomeTemplate != null) {
            finStatements[incomeFinStatementIndex].templateAndAccountsMapping.accounts = incomeAccounts;
            finStatements[incomeFinStatementIndex].templateAndAccountsMapping.template = incomeTemplate;
        }
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.ADJ_AND_REC_TIME_SPENT_TILL_COMPLETION);

        if (this.currentAnnualReport == null || this.balanceEngine == null || this.incomeEngine == null) {
            throw new Error('Current annual is null or balance/income engines');
        }
        this.isSaveRequestInProgress = true;
        this.updateCurrentAnnualReport(this.currentAnnualReportFinStatements, this.balanceEngine, this.incomeEngine);
        this.dispatchFinStatementChanges(this.currentAnnualReportFinStatements);
        return of(void 0);
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            // TODO: need investigation why it gives false using fastDeepEqual(this.balanceEngine, this.originalBalanceEngine)
            const balanceAndIncomeEnginesEqualTheOriginal =
                fastDeepEqual(JSON.stringify(this.balanceEngine), JSON.stringify(this.originalBalanceEngine)) &&
                fastDeepEqual(JSON.stringify(this.incomeEngine), JSON.stringify(this.originalIncomeEngine));
            return of(!balanceAndIncomeEnginesEqualTheOriginal);
        }
    }

    protected onSaveSuccessfully(): void {
        this.originalBalanceEngine = cloneDeep<AccountMappingEngine | undefined | null>(this.balanceEngine);
        this.originalIncomeEngine = cloneDeep<AccountMappingEngine | undefined | null>(this.incomeEngine);

        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(true);
        this.fireSuccessNotification();
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    //#region account mapping related

    private createAccountMappingInput(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        const generalInformation = annualReport.generalInformation;

        if (generalInformation == null) {
            throw new Error('Generla information is null');
        }

        const balanceInterpretation: IFinancialStatementInterpretation =
            finStatements[balanceFinStatementIndex].import.interpretation;
        const incomeInterpretation: IFinancialStatementInterpretation =
            finStatements[incomeFinStatementIndex].import.interpretation;
        this.accountMappingInput = {
            balance: this.financialStatementMapper.mapFinStatement(finStatements[balanceFinStatementIndex]),
            income: this.financialStatementMapper.mapFinStatement(finStatements[incomeFinStatementIndex]),
            dates: {
                balanceStartDate: generalInformation.noPreviousData ? '-' : generalInformation.balancePreviousYearDate,
                balanceEndDate: generalInformation.balanceSheetDate,
                incomeStartDate: generalInformation.noPreviousData
                    ? '-'
                    : this.dateLocalPipe.transform(generalInformation.incomePreviousYearFromDate).toString() +
                    '-' +
                    (generalInformation.incomePreviousYearToDate != null
                        ? this.dateLocalPipe.transform(generalInformation.incomePreviousYearToDate).toString()
                        : ''),
                incomeEndDate:
                    this.dateLocalPipe.transform(generalInformation.incomeCurrentYearFromDate).toString() +
                    '-' +
                    (generalInformation.incomeCurrentYearToDate != null
                        ? this.dateLocalPipe.transform(generalInformation.incomeCurrentYearToDate).toString()
                        : ''),
            },
            sheetSystem: {
                numberFormat: balanceInterpretation.numberFormat,
                assets: balanceInterpretation.assets,
                liabilities: balanceInterpretation.liabilities,
                income: incomeInterpretation.income,
                expenses: incomeInterpretation.expenses,
            },
        };
        this.currency = generalInformation.currency?.name;
    }

    private prepareEngines(value: IAccountMappingFormInput): void {
        debug('value', value);

        this.balanceEngine = this.financialStatementMapper.getWorkingEngineFromData(
            value.balance,
            value.sheetSystem,
            {
                startDate: value.dates.balanceStartDate,
                endDate: value.dates.balanceEndDate,
            },
            this.currentLanguage,
            this.defaultLanguage,
            this.currentAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReport,
        );

        this.originalBalanceEngine = cloneDeep<AccountMappingEngine | undefined | null>(this.balanceEngine);

        this.incomeEngine = this.financialStatementMapper.getWorkingEngineFromData(
            value.income,
            value.sheetSystem,
            {
                startDate: value.dates.incomeStartDate,
                endDate: value.dates.incomeEndDate,
            },
            this.currentLanguage,
            this.defaultLanguage,
            this.currentAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReport,
        );
        this.originalIncomeEngine = cloneDeep<AccountMappingEngine | undefined | null>(this.incomeEngine);

        if (this.balanceEngine == null || this.incomeEngine == null) {
            this.dataState = DataState.Error;
            return;
        }

        this.dataState = DataState.Success;
        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
    }

    copyChangeInAdjustmentsToIncomeAccount(changeInAdjustmentValue: number): void {
        if (this.IncomeAccountsOptions == null || this.IncomeAccountsOptions.length === 0) {
            this.prepareIncomeAccountsOptions();
        }

        this.openIncomeAccountPopup()
            .pipe(
                first(),
                filter((result) => result != null),
                untilDestroyed(this),
            )
            .subscribe((pickedIncomeAccount) =>
                this.handleCopyChangeInAdjustmentToIncomeAccount(pickedIncomeAccount, changeInAdjustmentValue),
            );
    }

    private prepareIncomeAccountsOptions(): void {
        if (this.incomeEngine == null) {
            throw new Error('The income engine is null, no mapping is saved or there is no template');
        }

        this.IncomeAccountsOptions = [...this.incomeEngine.selectedAccounts];
    }

    private openIncomeAccountPopup(): Observable<IAccount> {
        const dialogRef = this.aumDialog.open<
            IncomeAccountSelectionPopupComponent,
            IIncomeAccountSelectionDialogData,
            IAccount | undefined
        >(IncomeAccountSelectionPopupComponent, {
            width: '250px',
            data: {
                incomeAccounts: this.IncomeAccountsOptions,
                incomeCurrentYearFromDate: this.currentAnnualReport.generalInformation.incomeCurrentYearFromDate,
                incomeCurrentYearToDate: this.currentAnnualReport.generalInformation.incomeCurrentYearToDate,
                incomePreviousYearFromDate: this.currentAnnualReport.generalInformation.incomePreviousYearFromDate,
                incomePreviousYearToDate: this.currentAnnualReport.generalInformation.incomePreviousYearToDate,
            },
        });

        return dialogRef.afterClosed();
    }

    private handleCopyChangeInAdjustmentToIncomeAccount(
        pickedAccount: IAccount,
        changeInAdjustmentValue: number,
    ): void {
        if (this.incomeEngine == null) {
            throw new Error('The income engine is null, no mapping is saved or there is no template');
        }
        pickedAccount.closingAdjustment = changeInAdjustmentValue;
        this.incomeEngine.updateAccount(pickedAccount);
        this.shouldIncomeRecalculate = true;
    }

    handleChangeOfTabs(event: MatTabChangeEvent): void {
        if (event.index === balanceFinStatementIndex) {
            this.shouldIncomeRecalculate = false;
        }
    }

    protected override isDataConsistent(): Observable<ConsistencyCheckResult> {
        if (
            this.currentAnnualReport == null ||
            this.currentAnnualReportFinStatements.length === 0 ||
            this.balanceEngine == null ||
            this.incomeEngine == null
        ) {
            return of({ isDataConsistent: true, warnings: [] });
        }

        const latestBalanceTemplate = this.balanceEngine.getMappedTemplate();
        const latestIncomeTemplate = this.incomeEngine.getMappedTemplate();

        if (
            latestBalanceTemplate.profitOfTheYear != null &&
            EqualityChecksHelper.findProfitFliInBalance(latestBalanceTemplate) == null
        ) {
            this.fireErrorMessage('aumico_reports_error_profit_fli_not_found');
        }

        let totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult: EqualityChecksResult;
        try {
            totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult =
                EqualityChecksHelper.checkTotalReclassesAssetsEqualTotalReclassesLiabilities(latestBalanceTemplate);

        } catch (error) {
            console.error('Error during equality check', error);
            totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult = EqualityChecksResult.BOTH_EQUAL;
        }

        const totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResult =
            EqualityChecksHelper.checkTotalAdjustmentsAssetsEqualTotalAdjustmentsLiabilities(latestBalanceTemplate);

        let TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult: EqualityChecksResult;
        try {
            TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult =
                EqualityChecksHelper.checkTotalReclassesIncomeEqualTotalReclassesProfitFLI(
                    latestBalanceTemplate,
                    latestIncomeTemplate,
                );
        } catch (error) {
            console.error('Error during equality check', error);
            TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult = EqualityChecksResult.BOTH_EQUAL;
        }

        let TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult: EqualityChecksResult;
        try {
            TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult =
                EqualityChecksHelper.checkTotalAdjustmentsIncomeEqualTotalAdjustmentsProfitFLI(
                    latestBalanceTemplate,
                    latestIncomeTemplate,
                );

        } catch (error) {
            console.error('Error during equality check', error);
            TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult = EqualityChecksResult.BOTH_EQUAL;
        }

        const isDataInconsistent =
            totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult !== EqualityChecksResult.BOTH_EQUAL ||
            totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResult !==
            EqualityChecksResult.BOTH_EQUAL ||
            TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult !== EqualityChecksResult.BOTH_EQUAL ||
            TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult !== EqualityChecksResult.BOTH_EQUAL;

        const warnings = this.getEqualityCheckDialogLocalizationKey(
            totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult,
            totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResult,
            TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult,
            TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult,
        );

        return of({
            isDataConsistent: !isDataInconsistent,
            warnings: warnings,
        });
    }

    private getEqualityCheckDialogLocalizationKey(
        totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult: EqualityChecksResult,
        totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResult: EqualityChecksResult,
        TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult: EqualityChecksResult,
        TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult: EqualityChecksResult,
    ): string[] {
        let totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey: string | undefined;
        let totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey: string | undefined;
        let totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey: string | undefined;
        let totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey: string | undefined;

        const warningsList: string[] = [];

        switch (totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResult) {
            case EqualityChecksResult.BOTH_NOT_EQUAL:
                totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_assets_and_total_reclasses_liabilities_opening_and_closing_not_equal';
                warningsList.push(totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.CLOSING_NOT_EQUAL:
                totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_assets_and_total_reclasses_liabilities_closing_not_equal';
                warningsList.push(totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.OPENING_NOT_EQUAL:
                totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_assets_and_total_reclasses_liabilities_opening_not_equal';
                warningsList.push(totalReclassesAssetsAndTotalReclassesLiabilitiesEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.BOTH_EQUAL:
                // in case both are equalt then there is no warning to add to the list
                break;
        }

        switch (totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResult) {
            case EqualityChecksResult.BOTH_NOT_EQUAL:
                totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_assets_and_total_adjustments_liabilities_opening_and_closing_not_equal';
                warningsList.push(
                    totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey,
                );
                break;
            case EqualityChecksResult.CLOSING_NOT_EQUAL:
                totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_assets_and_total_adjustments_liabilities_closing_not_equal';
                warningsList.push(
                    totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey,
                );
                break;
            case EqualityChecksResult.OPENING_NOT_EQUAL:
                totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_assets_and_total_adjustments_liabilities_opening_not_equal';
                warningsList.push(
                    totalAdjustmentsAssetsAndTotalAdjustmentsLiabilitiesEqualityCheckResultLocalizationKey,
                );
                break;
            case EqualityChecksResult.BOTH_EQUAL:
                // in case both are equalt then there is no warning to add to the list
                break;
        }

        switch (TotalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResult) {
            case EqualityChecksResult.BOTH_NOT_EQUAL:
                totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_income_and_total_reclasses_profit_fli_opening_and_closing_not_equal';
                warningsList.push(totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.CLOSING_NOT_EQUAL:
                totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_income_and_total_reclasses_profit_fli_closing_not_equal';
                warningsList.push(totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.OPENING_NOT_EQUAL:
                totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_reclasses_income_and_total_reclasses_profit_fli_opening_not_equal';
                warningsList.push(totalReclassesIncomeAndTotalReclassesProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.BOTH_EQUAL:
                // in case both are equalt then there is no warning to add to the list
                break;
        }

        switch (TotalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResult) {
            case EqualityChecksResult.BOTH_NOT_EQUAL:
                totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_income_and_total_adjustments_profit_fli_opening_and_closing_not_equal';
                warningsList.push(totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.CLOSING_NOT_EQUAL:
                totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey =
                    totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_income_and_total_adjustments_profit_fli_closing_not_equal';
                warningsList.push(totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.OPENING_NOT_EQUAL:
                totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey =
                    'aumico_reports_equality_check_dialog_total_adjustments_income_and_total_adjustments_profit_fli_opening_not_equal';
                warningsList.push(totalAdjustmentsIncomeAndTotalAdjustmentsProfitFliEqualityCheckResultLocalizationKey);
                break;
            case EqualityChecksResult.BOTH_EQUAL:
                // in case both are equalt then there is no warning to add to the list
                break;
        }

        return warningsList;
    }
}
